import LayoutSettings from '@/components/LayoutSetting/LayoutSettings';
import { config } from '@/config';
import { DEFAULT_WEEKLY_WORKING_DATA } from '@/constants';
import { KindOfTimeType } from '@/constants/enum';
import { deliverySettingSelector, deliveryWeeklyWorkingDaySelector } from '@/redux/slice/delivery.slice';
import { BlockStack } from '@shopify/polaris';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import DeliveryPrepareTime from './DeliveryPrepareTime';
import DeliveryDateRange from './DeliveryDateRange';

interface VisualDeliveryDayProps {
  time24h: { id: number; value: string }[];
}

const VisualDeliveryDays: FC<VisualDeliveryDayProps> = ({ time24h }) => {
  const { t } = useTranslation('deliverySettings');

  const [selectTabDays, setSelectTabDays] = useState<number>(0);
  const deliverySettings = useSelector(deliverySettingSelector);
  const deliveryWeeklyWorkingDays = useSelector(deliveryWeeklyWorkingDaySelector);

  const pickupDayLabels = useMemo(() => {
    return [
      t('sections.weekDaysShort.0'), //sunday
      t('sections.weekDaysShort.1'), //monday
      t('sections.weekDaysShort.2'), //tuesday
      t('sections.weekDaysShort.3'), //wednesday
      t('sections.weekDaysShort.4'), //thursday
      t('sections.weekDaysShort.5'), //friday
      t('sections.weekDaysShort.6'), //saturday
    ];
  }, [t]);

  const workingDayTabs = useMemo(() => {
    return DEFAULT_WEEKLY_WORKING_DATA.filter((item) => item.isEnable && item.day >= 0)
      .sort((a, b) => {
        const dayA = a.day === 0 ? 7 : a.day;
        const dayB = b.day === 0 ? 7 : b.day;
        return dayA - dayB;
      })
      .map((item) => ({
        id: item.day.toString(),
        content: pickupDayLabels[item.day],
        panelID: item.day.toString(),
      }));
  }, [pickupDayLabels]);

  const selectedWorkingDay = useMemo(() => {
    if (workingDayTabs.length && deliverySettings.deliveryPreparationTimeType === KindOfTimeType.DIFFERENT) {
      return deliveryWeeklyWorkingDays.find((item) => item.day === Number(workingDayTabs[selectTabDays]?.id));
    }

    return deliveryWeeklyWorkingDays.find((item) => item.day === config.SAME_DAY_VALUE);
  }, [deliverySettings.deliveryPreparationTimeType, deliveryWeeklyWorkingDays, selectTabDays, workingDayTabs]);

  return (
    <LayoutSettings title={t('sections.visualDeliveryDays.title')} helpText={t('sections.visualDeliveryDays.helpText')}>
      <BlockStack gap={'200'}>
        {selectedWorkingDay && (
          <DeliveryPrepareTime
            selectedWorkingDay={selectedWorkingDay}
            selectTabDays={selectTabDays}
            setSelectTabDays={setSelectTabDays}
            workingDayTabs={workingDayTabs}
            time24h={time24h}
          />
        )}
        <DeliveryDateRange />
      </BlockStack>
    </LayoutSettings>
  );
};

export default VisualDeliveryDays;
